import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  KnowledgeResourceSchema,
  KnowledgeContainerSchema,
  KnowledgeItemSchema,
  CreateMappingRequestSchema,
  MappingResponseSchema,
  ContentSourceStatusSchema,
  AppKnowledgeSummarySchema,
  KnowledgeSyncProviderSchema,
  ConnectionStatusSchema,
  BoxCcgConnectBodySchema,
} from './knowledge-sync.schema';
import { KNOWLEDGE_SYNC_PATH_NAME } from './knowledge-sync.constants';

export const KnowledgeSyncContract = terraContract
  .prefix(`/${KNOWLEDGE_SYNC_PATH_NAME}`)
  .router({
    listResources: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/resources',
        summary: 'List available resources for a provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
        }),
      )
      .output(
        z.object({
          resources: z.array(KnowledgeResourceSchema),
        }),
      ),
    listContainers: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/containers',
        summary: 'List available containers (spaces, databases) for a provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
          query: z.object({
            search: z.string().optional(),
            resourceId: z.string().optional(),
            parentId: z
              .string()
              .optional()
              .describe(
                'Parent container ID for nested browsing (e.g. subfolder listing)',
              ),
            cursor: z.string().optional(),
            limit: z.coerce.number().optional().prefault(50),
          }),
        }),
      )
      .output(
        z.object({
          containers: z.array(KnowledgeContainerSchema),
          nextCursor: z.string().nullable(),
        }),
      ),
    listContainerItems: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/containers/{containerId}/items',
        summary: 'List items within a container',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
            containerId: z.string(),
          }),
          query: z.object({
            resourceId: z.string().optional(),
            cursor: z.string().optional(),
            limit: z.coerce.number().optional().prefault(50),
          }),
        }),
      )
      .output(
        z.object({
          items: z.array(KnowledgeItemSchema),
          nextCursor: z.string().nullable(),
        }),
      ),
    searchItems: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/items/search',
        summary: 'Search items across all containers for a provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
          query: z.object({
            q: z.string().min(1),
            resourceId: z.string().optional(),
            cursor: z.string().optional(),
            limit: z.coerce.number().optional().prefault(50),
          }),
        }),
      )
      .output(
        z.object({
          items: z.array(KnowledgeItemSchema),
          nextCursor: z.string().nullable(),
        }),
      ),
    getSourceStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/status',
        summary: 'Get sync status for a content source',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
        }),
      )
      .output(ContentSourceStatusSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    createMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/mappings',
        summary: 'Map knowledge source scopes to an application',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateMappingRequestSchema }))
      .output(
        z.object({
          mappings: z.array(MappingResponseSchema),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    deleteMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/mappings/{mappingId}',
        summary: 'Remove a knowledge source mapping (triggers cleanup)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            mappingId: z.string(),
          }),
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getAppKnowledgeSummary: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/applications/{applicationId}/summary',
        summary: 'Get knowledge sync summary for an application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
          }),
        }),
      )
      .output(
        z.object({
          sources: z.array(AppKnowledgeSummarySchema),
        }),
      ),
    getConnections: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/connections',
        summary:
          'Get all KB provider connection statuses for the current tenant',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          connections: z.array(ConnectionStatusSchema),
        }),
      ),
    getConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/connections/{provider}',
        summary: 'Get connection status for a specific KB provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
        }),
      )
      .output(ConnectionStatusSchema),
    getOAuthAuthorizeUrl: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/oauth/{provider}/authorize',
        summary: 'Get OAuth authorize URL for a KB provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
          query: z.object({
            instanceUrl: z.string().optional(),
            openerOrigin: z.string().optional(),
          }),
        }),
      )
      .output(
        z.object({
          authorizeUrl: z.string(),
        }),
      ),
    exchangeOAuthCode: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/oauth/exchange',
        summary: 'Exchange OAuth authorization code for a KB provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          body: z.object({
            provider: KnowledgeSyncProviderSchema,
            code: z.string(),
            state: z.string(),
          }),
        }),
      )
      .output(z.object({ success: z.boolean(), provider: z.string() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    connectBoxCcg: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/box/ccg/connect',
        summary: 'Connect Box with a customer-owned CCG app',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BoxCcgConnectBodySchema }))
      .output(z.object({ success: z.boolean(), provider: z.string() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    disconnectProvider: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/connections/{provider}',
        summary: 'Disconnect (revoke) a KB provider credential',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            provider: KnowledgeSyncProviderSchema,
          }),
        }),
      )
      .output(z.object({ success: z.boolean() })),
    triggerSync: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/sources/{sourceId}/sync',
        summary: 'Manually trigger a sync for a knowledge source',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            sourceId: z.string(),
          }),
        }),
      )
      .output(z.object({ sourceId: z.string(), queued: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
  });
